import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Tab, Tabs } from '@mui/material'
import BackHeader from '../../components/BackHeader/BackHeader'
import { fineBoxPayUrl, initialsFrom } from './logic'
import { useHold } from './store'
import Loading from '../../components/Loading/Loading'
import { HoldScreen, SeasonSwitch } from './bits'
import { Confirm, CreateFineModal, EditFineModal, FineBoxLinkModal, FineBoxModal, GiveFineModal, PayModal } from './FineModals'

function fineWhen(iso) {
  const date = new Date(iso)
  if (!iso || Number.isNaN(date.getTime())) return ''
  const day = new Intl.DateTimeFormat('da-DK', { day: 'numeric', month: 'short', year: 'numeric' }).format(date)
  const time = new Intl.DateTimeFormat('da-DK', { hour: '2-digit', minute: '2-digit' }).format(date)
  return `${day} kl. ${time}`
}

function personBalance(season, mateId) {
  const fines = (season.fines || []).filter((fine) => fine.mateId === mateId)
  const given = fines.reduce((sum, fine) => sum + fine.amount, 0)
  const recorded = (season.finePayments || []).find((item) => item.mateId === mateId)
  const paidRaw = recorded
    ? recorded.amount
    : fines.reduce((sum, fine) => sum + (fine.paid ? fine.amount : 0), 0)
  const paid = Math.max(0, paidRaw)
  return { paid, owed: given - paid }
}

function seasonBalance(season) {
  const ids = new Set((season.fines || []).map((fine) => fine.mateId))
  return [...ids].reduce((sum, id) => {
    const row = personBalance(season, id)
    sum.paid += row.paid
    sum.owed += row.owed
    return sum
  }, { paid: 0, owed: 0 })
}

async function run(dispatch, onError, action) {
  const result = await dispatch(action)
  onError(result?.ok ? '' : (result?.error || 'Det kunne ikke gemmes'))
  return Boolean(result?.ok)
}


function fineTitle(fine) {
  return fine.count > 1 ? `${fine.count} x ${fine.text}` : fine.text
}

function groupFines(fines) {
  const groups = []
  const index = new Map()
  fines.forEach((fine) => {
    const unit = Number(fine.amount) || 0
    const place = fine.matchId || fine.createdAt || fine.id
    const key = [fine.mateId, place, fine.typeId || fine.text, unit, fine.paid ? 1 : 0].join('|')
    const existing = index.get(key)
    if (!existing) {
      const group = { ...fine, count: 1, ids: [fine.id], unit }
      index.set(key, group)
      groups.push(group)
      return
    }
    existing.count += 1
    existing.ids.push(fine.id)
  })
  return groups
}

function fineTotal(fine) {
  return fine.unit * fine.count
}

function fineAmountOk(amount) {
  const value = Math.round(Number(amount))
  return Number.isInteger(value) && value !== 0 && Math.abs(value) <= 10000
}

function fineCountOk(count) {
  const value = Math.round(Number(count))
  return Number.isInteger(value) && value >= 1 && value <= 20
}

async function saveFineEdit(dispatch, onError, teamId, ids, text, amount, count) {
  return run(dispatch, onError, {
    type: 'UPDATE_FINE_GROUP',
    teamId,
    fineIds: ids,
    text: text.trim(),
    amount: Math.round(Number(amount)),
    count: Math.round(Number(count)),
  })
}

function FineRows({ fines, season, team, isChair, dispatch, onError, onDelete, onEdit }) {
  const rows = groupFines(fines)
  if (rows.length === 0) return <p className="hold-empty">Ingen bøder.</p>
  return (
    <ul className="hold-fine-list">
      {rows.map((fine) => {
        const match = fine.matchId ? season.matches.find((item) => item.id === fine.matchId) : null
        const where = match ? `${match.home ? 'Hjemme' : 'Ude'} mod ${match.opponent}` : 'Uden kamp'
        const title = fineTitle(fine)
        return (
          <li key={fine.ids.join('-')} className="hold-fine-row">
            <div className="hold-fine-copy">
              <span>{title}</span>
              <span className="hold-meta">{fineTotal(fine)} kr · {where}</span>
            </div>
            <div className="hold-fine-actions">
              {isChair ? (
                <button
                  type="button"
                  className={`hold-action hold-action--small ${fine.paid ? 'hold-action--save' : 'hold-action--ghost'}`}
                  onClick={async () => {
                    const next = !fine.paid
                    for (const fineId of fine.ids) {
                      const ok = await run(dispatch, onError, {
                        type: 'SET_FINE_PAID',
                        teamId: team.id,
                        fineId,
                        paid: next,
                      })
                      if (!ok) return
                    }
                  }}
                >
                  {fine.paid ? 'Betalt' : 'Marker betalt'}
                </button>
              ) : (
                <span className={fine.paid ? 'hold-meta' : 'hold-fine-open'}>{fine.paid ? 'Betalt' : 'Skyldig'}</span>
              )}
              {isChair && season.current ? (
                <>
                  <button type="button" className="hold-icon-button" aria-label={`Ret ${title}`} onClick={() => onEdit(fine)}>
                    <EditOutlinedIcon />
                  </button>
                  <button type="button" className="hold-icon-button hold-icon-button--delete" aria-label={`Slet ${title}`} onClick={() => onDelete(fine)}>
                    <DeleteOutlineIcon />
                  </button>
                </>
              ) : null}
            </div>
          </li>
        )
      })}
    </ul>
  )
}


function TypeRow({ type, used, teamId, dispatch, onError, onDelete }) {
  const textRef = useRef(null)
  const [text, setText] = useState(type.text)
  const [amount, setAmount] = useState(String(type.amount))
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const changed = text.trim() !== type.text || (Number(amount) || 0) !== type.amount
  const canSave = changed && !busy && text.trim() && Number.isInteger(Math.round(Number(amount))) && Math.round(Number(amount)) !== 0 && Math.abs(Math.round(Number(amount))) <= 10000

  useEffect(() => {
    setText(type.text)
    setAmount(String(type.amount))
  }, [type.text, type.amount])

  useEffect(() => {
    if (editing) textRef.current?.focus()
  }, [editing])

  const close = () => {
    setText(type.text)
    setAmount(String(type.amount))
    setEditing(false)
  }

  if (!editing) {
    return (
      <li className="hold-type-row">
        <div className="hold-type-face">
          <button type="button" className="hold-type-open" aria-label={`Ret ${type.text}`} onClick={() => setEditing(true)}>
            <span className="hold-type-text">{type.text}</span>
            <span className="hold-type-price">{type.amount} kr</span>
          </button>
          <button
            type="button"
            className="hold-icon-button hold-icon-button--delete"
            aria-label={`Slet ${type.text}`}
            onClick={() => onDelete(used ? { ...type, blocked: true } : type)}
          >
            <DeleteOutlineIcon />
          </button>
        </div>
      </li>
    )
  }

  return (
    <li className="hold-type-row">
      <form
        className="hold-type-edit"
        onSubmit={async (event) => {
          event.preventDefault()
          if (!canSave) return
          setBusy(true)
          const ok = await run(dispatch, onError, {
            type: 'UPDATE_FINE_TYPE',
            teamId,
            typeId: type.id,
            text: text.trim(),
            amount: Math.round(Number(amount)),
          })
          setBusy(false)
          if (ok) setEditing(false)
        }}
      >
        <div className="hold-type-fields">
          <label>
            Bøde
            <input ref={textRef} value={text} maxLength={80} onChange={(event) => setText(event.target.value)} aria-label="Bødetekst" />
          </label>
          <label>
            Pris
            <span className="hold-type-amount">
              <input value={amount} type="number" step="1" inputMode="text" onChange={(event) => setAmount(event.target.value)} aria-label="Pris i kroner" />
              <span>kr</span>
            </span>
          </label>
        </div>
        <div className="hold-type-actions">
          <button type="button" className="hold-text-button" onClick={close} disabled={busy}>Annuller</button>
          <button type="submit" className="hold-action hold-action--small hold-action--save" disabled={!canSave}>
            {busy ? 'Gemmer…' : 'Gem'}
          </button>
        </div>
      </form>
    </li>
  )
}

export function MatchFines({ team, season, match }) {
  const { me, dispatch, nameOf } = useHold()
  const isChair = Boolean(me?.fineChair)
  const [error, setError] = useState('')
  const [pending, setPending] = useState(null)
  const [busy, setBusy] = useState(false)
  const [giveOpen, setGiveOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [editText, setEditText] = useState('')
  const [editAmount, setEditAmount] = useState('')
  const [editCount, setEditCount] = useState('1')
  const [editBusy, setEditBusy] = useState(false)
  const fines = (season.fines || []).filter((fine) => fine.matchId === match.id)
  const canGive = isChair && season.current
  if (fines.length === 0 && !canGive) return null

  return (
    <section className="hold-stack">
      <h2>Bøder</h2>
      {error ? <p className="hold-note">{error}</p> : null}
      {fines.length === 0 ? null : (
        <FineRows
          fines={fines}
          season={season}
          team={team}
          isChair={isChair}
          dispatch={dispatch}
          onError={setError}
          onDelete={setPending}
          onEdit={(fine) => {
            setEditing(fine)
            setEditText(fine.text)
            setEditAmount(String(fine.unit))
            setEditCount(String(fine.count))
          }}
        />
      )}
      {canGive ? (
        <>
          <button type="button" className="hold-action hold-action--ghost" onClick={() => setGiveOpen(true)}>
            Giv bøde
          </button>
          <GiveFineModal
            open={giveOpen}
            team={team}
            season={season}
            matchId={match.id}
            nameOf={nameOf}
            dispatch={dispatch}
            onError={setError}
            onClose={() => setGiveOpen(false)}
          />
        </>
      ) : null}
      {pending ? (
        <Confirm
          title="Slet bøde"
          body={`${fineTitle(pending)} kan ikke fortrydes.`}
          busy={busy}
          onCancel={() => setPending(null)}
          onConfirm={async () => {
            setBusy(true)
            const ids = pending.ids || [pending.id]
            let ok = true
            for (const fineId of ids) {
              ok = await run(dispatch, setError, { type: 'DELETE_FINE', teamId: team.id, fineId })
              if (!ok) break
            }
            setBusy(false)
            if (ok) setPending(null)
          }}
        />
      ) : null}
      {editing ? (
        <EditFineModal
          open
          text={editText}
          amount={editAmount}
          count={editCount}
          busy={editBusy}
          onText={setEditText}
          onAmount={setEditAmount}
          onCount={setEditCount}
          onClose={() => {
            if (editBusy) return
            setEditing(null)
          }}
          onSubmit={async (event) => {
            event.preventDefault()
            if (!fineAmountOk(editAmount) || !fineCountOk(editCount) || !editText.trim()) return
            setEditBusy(true)
            const ok = await saveFineEdit(dispatch, setError, team.id, editing.ids, editText, editAmount, editCount)
            setEditBusy(false)
            if (ok) setEditing(null)
          }}
        />
      ) : null}
    </section>
  )
}

function FineLines({ fines, season, name, canManage = false, onEdit, onDelete, approvals = [], nameOf }) {
  const rows = [
    ...groupFines(fines).map((fine) => ({ type: 'fine', at: fine.createdAt, fine })),
    ...approvals.map((payment) => ({ type: 'payment', at: payment.createdAt, payment })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at))
  if (rows.length === 0) return null
  return (
    <ul className="hold-fine-lines">
      {rows.map((row) => {
        if (row.type === 'payment') {
          const payment = row.payment
          const known = nameOf(payment.approvedBy)
          const who = known !== 'Ukendt' ? known : (payment.approvedByName || 'Ukendt')
          const title = payment.kind === 'registered' ? 'Registreret betaling' : 'Godkendt betaling'
          return (
            <li key={payment.id} className="is-payment">
              <div>
                <span>{title}</span>
                <span className="hold-fine-line-name">af {who}</span>
                <time dateTime={payment.createdAt}>{fineWhen(payment.createdAt)}</time>
              </div>
              <span className="hold-fine-line-side">
                <span className="hold-fine-line-amount">{payment.amount} kr</span>
              </span>
            </li>
          )
        }
        const fine = row.fine
        const match = fine.matchId ? season.matches.find((item) => item.id === fine.matchId) : null
        const where = match ? `${match.home ? 'Hjemme' : 'Ude'} mod ${match.opponent}` : ''
        const title = fineTitle(fine)
        return (
          <li key={fine.ids.join('-')} className={fine.paid ? 'is-paid' : ''}>
            <div>
              <span>{title}</span>
              <span className="hold-fine-line-name">{name}</span>
              <time dateTime={fine.createdAt}>
                {fineWhen(fine.createdAt)}{where ? ` · ${where}` : ''}
              </time>
            </div>
            <span className="hold-fine-line-side">
              <span className="hold-fine-line-amount">
                {fineTotal(fine)} kr
                {fine.paid ? <em>Betalt</em> : null}
              </span>
              {canManage ? (
                <>
                  <button type="button" className="hold-icon-button" aria-label={`Ret ${title}`} onClick={() => onEdit(fine)}>
                    <EditOutlinedIcon />
                  </button>
                  <button type="button" className="hold-icon-button hold-icon-button--delete" aria-label={`Slet ${title}`} onClick={() => onDelete(fine)}>
                    <DeleteOutlineIcon />
                  </button>
                </>
              ) : null}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

function peopleInBox(team, season, nameOf) {
  const ids = new Set(team.members.filter((member) => member.inFineBox).map((member) => member.id))
  new Set((season.fines || []).map((fine) => fine.mateId)).forEach((id) => {
    if (personBalance(season, id).owed > 0) ids.add(id)
  })
  return [...ids].sort((a, b) => nameOf(a).localeCompare(nameOf(b), 'da'))
}

export default function FineView() {
  const navigate = useNavigate()
  const { team, season, me, meId, dispatch, nameOf, loading } = useHold()
  const [tab, setTab] = useState('skyld')
  const [openPerson, setOpenPerson] = useState('')
  const [error, setError] = useState('')
  const [pending, setPending] = useState(null)
  const [busy, setBusy] = useState(false)
  const [draftText, setDraftText] = useState('')
  const [draftAmount, setDraftAmount] = useState('')
  const [creating, setCreating] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [giveOpen, setGiveOpen] = useState(false)
  const [payOpen, setPayOpen] = useState(false)
  const [joinOpen, setJoinOpen] = useState(false)
  const [leaveOpen, setLeaveOpen] = useState(false)
  const [boxBusy, setBoxBusy] = useState(false)
  const [payBusy, setPayBusy] = useState(false)
  const [claimBusy, setClaimBusy] = useState('')
  const [boxOpen, setBoxOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [editText, setEditText] = useState('')
  const [editAmount, setEditAmount] = useState('')
  const [editCount, setEditCount] = useState('1')
  const [editBusy, setEditBusy] = useState(false)
  const askedBox = useRef(false)
  useEffect(() => {
    if (loading || !me || askedBox.current) return
    askedBox.current = true
    if (!me.inFineBox) setJoinOpen(true)
  }, [loading, me])
  if (loading && !team) return <HoldScreen><Loading fill /></HoldScreen>
  if (!team || !season) return null

  const isChair = Boolean(me?.fineChair)
  const active = tab === 'formand' && !isChair ? 'skyld' : tab
  const usedTypeIds = new Set(team.seasons.flatMap((item) => (item.fines || []).map((fine) => fine.typeId)))
  const mine = (season.fines || []).filter((fine) => fine.mateId === meId)
  const people = peopleInBox(team, season, nameOf)
  const seasonSum = seasonBalance(season)
  const owed = personBalance(season, meId).owed
  const payUrl = fineBoxPayUrl({
    boxId: team.fineBoxId,
    amountKr: owed,
    message: `Bøder ${season.name} · ${nameOf(meId)}`,
  })
  const myClaim = (season.fineClaims || []).find((claim) => claim.mateId === meId) || null
  const claims = season.fineClaims || []

  return (
    <HoldScreen>
      <div className="hold-tabbed-top">
        <BackHeader
          eyebrow={team.name}
          title="Bødekasse"
          subtitle={season.name}
          onBack={() => navigate(`/hold/${team.id}`)}
          actions={isChair ? (
            <button type="button" className="hold-give-bar" onClick={() => setGiveOpen(true)}>
              Giv bøde
            </button>
          ) : null}
        />
        <div className="hold-tabbed-tabs hold-fine-tabs">
          <Tabs value={active} onChange={(_, value) => setTab(value)} variant="fullWidth" textColor="inherit">
            <Tab value="skyld" label="Mine bøder" />
            <Tab value="alle" label="Alle" />
            <Tab value="typer" label="Bødeliste" />
            {isChair ? <Tab value="formand" label={claims.length ? `Formand (${claims.length})` : 'Formand'} /> : null}
          </Tabs>
        </div>
      </div>

      {active === 'skyld' && payUrl ? (
        <button
          type="button"
          className="hold-action hold-fine-pay"
          disabled={payBusy}
          onClick={async () => {
            if (payBusy) return
            const same = myClaim && myClaim.amount === owed
            if (!same) {
              setPayBusy(true)
              const ok = await run(dispatch, setError, {
                type: 'CLAIM_FINE_PAYMENT',
                teamId: team.id,
                seasonId: season.id,
              })
              setPayBusy(false)
              if (!ok) return
            }
            window.location.assign(payUrl)
          }}
        >
          {payBusy ? 'Sender…' : myClaim && myClaim.amount === owed ? `Åbn MobilePay (${owed} kr)` : `Betal ${owed} kr med MobilePay`}
        </button>
      ) : null}
      {active === 'skyld' && myClaim ? <p className="hold-note hold-fine-wait">Afventer formandens godkendelse af {myClaim.amount} kr.</p> : null}
      {active === 'typer' ? null : <SeasonSwitch team={team} season={season} dispatch={dispatch} />}
      {active !== 'typer' && !season.current ? (
        <p className="hold-note">Sæsonen kan læses. Nye bøder gives i den aktuelle sæson.</p>
      ) : null}
      {error ? <p className="hold-note">{error}</p> : null}
      {isChair ? (
        <GiveFineModal
          open={giveOpen}
          team={team}
          season={season}
          nameOf={nameOf}
          dispatch={dispatch}
          onError={setError}
          onClose={() => setGiveOpen(false)}
        />
      ) : null}

      {active === 'skyld' ? (
        <>
          {mine.length === 0 && !(season.fineApprovals || []).some((item) => item.mateId === meId) ? <p className="hold-empty">Du har ingen bøder.</p> : (
            <div className="hold-fine-player hold-fine-sheet">
              <FineLines
                fines={mine}
                approvals={(season.fineApprovals || []).filter((item) => item.mateId === meId)}
                season={season}
                name={nameOf(meId)}
                nameOf={nameOf}
                canManage={isChair && season.current}
                onEdit={(fine) => {
                  setEditing(fine)
                  setEditText(fine.text)
                  setEditAmount(String(fine.unit))
                  setEditCount(String(fine.count))
                }}
                onDelete={setPending}
              />
            </div>
          )}
          {me?.inFineBox ? (
            <button type="button" className="hold-action hold-action--leave" onClick={() => setLeaveOpen(true)}>
              Forlad bødekassen
            </button>
          ) : null}
        </>
      ) : null}

      {active === 'alle' ? (
        <>
        <dl className="hold-fine-total">
          <div>
            <dt>Bøder</dt>
            <dd>{seasonSum.paid + seasonSum.owed} kr</dd>
          </div>
          <div>
            <dt>Indbetalt</dt>
            <dd className="is-paid">{seasonSum.paid} kr</dd>
          </div>
          <div>
            <dt>Manglende indbetaling</dt>
            <dd className={seasonSum.owed < 0 ? 'is-paid' : 'is-missing'}>{seasonSum.owed} kr</dd>
          </div>
        </dl>
        {people.length === 0 ? <p className="hold-empty">Ingen er med i bødekassen endnu.</p> : (
          <ul className="hold-fine-players">
            {people.map((id) => {
              const member = team.members.find((item) => item.id === id)
              const fines = season.fines.filter((fine) => fine.mateId === id)
              const approvals = (season.fineApprovals || []).filter((item) => item.mateId === id)
              const sum = personBalance(season, id)
              const name = nameOf(id)
              const status = !member ? 'Ikke på holdet' : (member.inFineBox ? '' : 'Ikke med')
              const open = openPerson === id
              const canOpen = fines.length > 0 || approvals.length > 0
              const tone = sum.owed <= 0 ? 'is-clear' : (sum.paid > 0 ? 'is-part' : 'is-none')
              const sums = (
                <span className="hold-fine-player-sums">
                  <span className="is-paid">Betalt {sum.paid} kr</span>
                  <span className={sum.owed < 0 ? 'is-paid' : 'is-missing'}>{sum.owed < 0 ? `Til gode ${-sum.owed} kr` : `Skylder ${sum.owed} kr`}</span>
                </span>
              )
              return (
                <li key={id} className={`hold-fine-player ${tone}`}>
                  {!canOpen ? (
                    <div className="hold-fine-player-head">
                      <span className="hold-fine-avatar" aria-hidden="true">{initialsFrom(name)}</span>
                      <span className="hold-fine-player-name">
                        {name}
                        {status ? <em>{status}</em> : null}
                      </span>
                      {sums}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="hold-fine-player-head"
                      aria-expanded={open}
                      onClick={() => setOpenPerson(open ? '' : id)}
                    >
                      <span className="hold-fine-avatar" aria-hidden="true">{initialsFrom(name)}</span>
                      <span className="hold-fine-player-name">
                        {name}
                        {status ? <em>{status}</em> : null}
                      </span>
                      {sums}
                    </button>
                  )}
                  {open ? (
                    <FineLines
                      fines={fines}
                      approvals={approvals}
                      season={season}
                      name={name}
                      nameOf={nameOf}
                      canManage={isChair && season.current}
                      onEdit={(fine) => {
                        setEditing(fine)
                        setEditText(fine.text)
                        setEditAmount(String(fine.unit))
                        setEditCount(String(fine.count))
                      }}
                      onDelete={setPending}
                    />
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
        </>
      ) : null}

      {active === 'typer' ? (
        <section className="hold-stack">
          <p className="hold-copy">Listen er den samme i alle sæsoner. En bøde, der allerede er givet, beholder teksten og prisen fra dengang.</p>
          {team.fineTypes.length === 0 ? <p className="hold-empty">Ingen bødetyper endnu.</p> : (
            <ul className="hold-fine-overview">
              {team.fineTypes.map((type) => (
                <li key={type.id} className="hold-fine-overview-row">
                  <span>{type.text}</span>
                  <strong>{type.amount} kr</strong>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {active === 'formand' ? (
        <div className="hold-chair">
          {claims.length > 0 ? (
            <section className="hold-chair-block">
              <h2>Godkend betaling</h2>
              <p className="hold-meta">Tjek boxen, før du godkender.</p>
              <ul className="hold-chair-claims">
                {claims.map((claim) => (
                  <li key={claim.id} className="hold-chair-claim">
                    <div className="hold-chair-claim-top">
                      <span>{nameOf(claim.mateId)}</span>
                      <strong>{claim.amount} kr</strong>
                    </div>
                    <div className="hold-chair-claim-actions">
                      <button
                        type="button"
                        className="hold-action hold-action--save"
                        disabled={claimBusy === claim.id}
                        onClick={async () => {
                          setClaimBusy(claim.id)
                          await run(dispatch, setError, {
                            type: 'DECIDE_FINE_CLAIM',
                            teamId: team.id,
                            claimId: claim.id,
                            approve: true,
                          })
                          setClaimBusy('')
                        }}
                      >
                        Godkend
                      </button>
                      <button
                        type="button"
                        className="hold-action hold-action--ghost"
                        disabled={claimBusy === claim.id}
                        onClick={async () => {
                          setClaimBusy(claim.id)
                          await run(dispatch, setError, {
                            type: 'DECIDE_FINE_CLAIM',
                            teamId: team.id,
                            claimId: claim.id,
                            approve: false,
                          })
                          setClaimBusy('')
                        }}
                      >
                        Afvis
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <div className="hold-chair-actions">
            <button type="button" className="hold-action hold-action--ghost hold-chair-box-button" onClick={() => setBoxOpen(true)}>
              MobilePay Box
            </button>
            <button type="button" className="hold-action" onClick={() => setCreateOpen(true)}>
              Opret bøde
            </button>
            <button type="button" className="hold-action hold-action--ghost" onClick={() => setPayOpen(true)}>
              Registrer betaling
            </button>
          </div>
          <FineBoxLinkModal
            open={boxOpen}
            team={team}
            dispatch={dispatch}
            onError={setError}
            onClose={() => setBoxOpen(false)}
          />
          <CreateFineModal
            open={createOpen}
            text={draftText}
            amount={draftAmount}
            busy={creating}
            onText={setDraftText}
            onAmount={setDraftAmount}
            onClose={() => {
              if (creating) return
              setCreateOpen(false)
            }}
            onSubmit={async (event) => {
                event.preventDefault()
                if (creating) return
                setCreating(true)
                const ok = await run(dispatch, setError, {
                  type: 'CREATE_FINE_TYPE',
                  teamId: team.id,
                  text: draftText.trim(),
                  amount: Math.round(Number(draftAmount) || 0),
                })
                setCreating(false)
                if (!ok) return
                setDraftText('')
                setDraftAmount('')
                setCreateOpen(false)
              }}
            />
          <PayModal
            open={payOpen}
            team={team}
            season={season}
            nameOf={nameOf}
            dispatch={dispatch}
            onError={setError}
            onClose={() => setPayOpen(false)}
          />
          <section className="hold-chair-block">
            <h2>Bødetyper</h2>
            <p className="hold-meta">Samme liste i alle sæsoner. En givet bøde beholder sin pris.</p>
            {team.fineTypes.length === 0 ? <p className="hold-empty">Ingen bødetyper endnu.</p> : (
              <ul className="hold-type-sheet">
                {team.fineTypes.map((type) => (
                  <TypeRow
                    key={type.id}
                    type={type}
                    used={usedTypeIds.has(type.id)}
                    teamId={team.id}
                    dispatch={dispatch}
                    onError={setError}
                    onDelete={setPending}
                  />
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : null}

      <FineBoxModal
        open={joinOpen}
        mode="join"
        busy={boxBusy}
        onClose={() => {
          if (boxBusy) return
          setJoinOpen(false)
        }}
        onConfirm={async () => {
          setBoxBusy(true)
          const ok = await run(dispatch, setError, { type: 'SET_MY_FINE_BOX', teamId: team.id, inFineBox: true })
          setBoxBusy(false)
          if (ok) setJoinOpen(false)
        }}
      />
      <FineBoxModal
        open={leaveOpen}
        mode="leave"
        busy={boxBusy}
        onClose={() => {
          if (boxBusy) return
          setLeaveOpen(false)
        }}
        onConfirm={async () => {
          setBoxBusy(true)
          const ok = await run(dispatch, setError, { type: 'SET_MY_FINE_BOX', teamId: team.id, inFineBox: false })
          setBoxBusy(false)
          if (ok) setLeaveOpen(false)
        }}
      />

      {pending ? (
        <Confirm
          title={pending.blocked ? 'Kan ikke slettes' : pending.mateId ? 'Slet bøde' : 'Slet type'}
          body={pending.blocked ? `${pending.text} er allerede givet og kan ikke slettes.` : `${pending.count > 1 ? `${pending.count} x ${pending.text}` : pending.text} kan ikke fortrydes.`}
          busy={busy}
          onCancel={() => setPending(null)}
          onConfirm={pending.blocked ? null : async () => {
            setBusy(true)
            let ok = true
            if (pending.ids?.length) {
              for (const fineId of pending.ids) {
                ok = await run(dispatch, setError, { type: 'DELETE_FINE', teamId: team.id, fineId })
                if (!ok) break
              }
            } else if (pending.mateId) {
              ok = await run(dispatch, setError, { type: 'DELETE_FINE', teamId: team.id, fineId: pending.id })
            } else {
              ok = await run(dispatch, setError, { type: 'DELETE_FINE_TYPE', teamId: team.id, typeId: pending.id })
            }
            setBusy(false)
            if (ok) setPending(null)
          }}
        />
      ) : null}
      {editing ? (
        <EditFineModal
          open
          text={editText}
          amount={editAmount}
          count={editCount}
          busy={editBusy}
          onText={setEditText}
          onAmount={setEditAmount}
          onCount={setEditCount}
          onClose={() => {
            if (editBusy) return
            setEditing(null)
          }}
          onSubmit={async (event) => {
            event.preventDefault()
            if (!fineAmountOk(editAmount) || !fineCountOk(editCount) || !editText.trim()) return
            setEditBusy(true)
            const ok = await saveFineEdit(dispatch, setError, team.id, editing.ids, editText, editAmount, editCount)
            setEditBusy(false)
            if (ok) setEditing(null)
          }}
        />
      ) : null}
    </HoldScreen>
  )
}
